/**
 * Pure formatting and parsing helpers.
 *
 * Framework-agnostic by design: no DOM, no state, no imports from the view
 * layer, so this is the part that would survive a UI rewrite unchanged.
 */
import { METRIC_KEYS, type MetricKey, type Workout } from '../types';

export function getExerciseDisplayName(name: string): string {
  if (name === 'pull-ups') return 'Pull-ups (palms away)';
  if (name === 'chin-ups') return 'Chin-ups (palms toward you)';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function shortDisplayName(name: string): string {
  if (name === 'pull-ups') return 'Pull-ups';
  if (name === 'chin-ups') return 'Chin-ups';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** "mm:ss" (or a bare seconds count) to seconds; null if unparseable. */
export function parseTime(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.includes(':')) {
    const [mm, ss] = trimmed.split(':');
    if (mm === undefined || ss === undefined) return null;
    if (mm.trim() === '' || ss.trim() === '') return null;
    const minutes = Number(mm);
    const seconds = Number(ss);
    if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
    return minutes * 60 + seconds;
  }
  if (trimmed === '') return null;
  const seconds = Number(trimmed);
  return Number.isFinite(seconds) ? seconds : null;
}

export function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** The one place the "20 reps, 45 lbs, 2:30" summary is built. */
export function formatWorkout(workout: Workout): string {
  const parts: string[] = [];
  if (workout.reps !== undefined) parts.push(`${workout.reps} reps`);
  if (workout.weight !== undefined) parts.push(`${workout.weight} lbs`);
  if (workout.time !== undefined) parts.push(workout.time);
  return parts.join(', ');
}

export function formatMetric(value: number, metric: MetricKey): string {
  if (metric === 'time') return formatTime(value);
  if (metric === 'weight') return `${Math.round(value * 10) / 10} lbs`;
  return `${Math.round(value)} reps`;
}

/**
 * One workout's value for a metric, or null when it did not record that
 * metric. Time is converted to seconds.
 */
export function numericValue(workout: Workout, metric: MetricKey): number | null {
  const raw = workout[metric];
  if (raw === undefined) return null;
  if (metric === 'time') return parseTime(String(raw));
  return typeof raw === 'number' ? raw : null;
}

/** Numeric series for a metric, skipping workouts that did not record it. */
export function numericValues(data: Workout[], metric: MetricKey): number[] {
  const out: number[] = [];
  for (const workout of data) {
    const value = numericValue(workout, metric);
    if (value !== null) out.push(value);
  }
  return out;
}

/**
 * Workouts oldest-first.
 *
 * Array order is NOT date order: mergeRemote appends records pulled from other
 * devices with .push(), regardless of when they happened. Anything that treats
 * position as chronology — "the latest workout", "progress since the first" —
 * must sort first, or a workout synced from another device silently becomes
 * your most recent.
 */
export function byDate(data: Workout[]): Workout[] {
  return [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
}

export function primaryMetric(metrics: MetricKey[]): MetricKey {
  return METRIC_KEYS.find((m) => metrics.includes(m)) ?? 'reps';
}

/** Exercise names are user-supplied and several views build HTML strings. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
